import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  Lock, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  Eye, 
  EyeOff,
  XCircle,
  Check
} from 'lucide-react';
import { API } from '../lib/api';

export default function ResetPasswordPage() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const token = searchParams.get('token');
  
  // Password requirements
  const requirements = [
    { 
      met: password.length >= 8, 
      text: language === 'en' ? 'At least 8 characters' : 'Almeno 8 caratteri' 
    },
    { 
      met: /[a-zA-Z]/.test(password), 
      text: language === 'en' ? 'At least one letter' : 'Almeno una lettera' 
    },
    { 
      met: /\d/.test(password), 
      text: language === 'en' ? 'At least one number' : 'Almeno un numero' 
    },
    { 
      met: password && confirmPassword && password === confirmPassword, 
      text: language === 'en' ? 'Passwords match' : 'Le password corrispondono' 
    }
  ];
  
  const allRequirementsMet = requirements.every(r => r.met);
  
  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setTokenError(language === 'en' 
        ? 'Reset token missing from URL'
        : 'Token di reset mancante nell\'URL');
      setValidating(false);
      return;
    }
    
    validateToken();
  }, [token]);
  
  const validateToken = async () => {
    setValidating(true);
    try {
      const response = await axios.get(`${API}/guest/validate-reset-token?token=${token}`);
      if (response.data.valid) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
        setTokenError(response.data.reason);
      }
    } catch (err) {
      setTokenValid(false);
      setTokenError(language === 'en' 
        ? 'Unable to validate token'
        : 'Impossibile validare il token');
    } finally {
      setValidating(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      setError(language === 'en' 
        ? 'Please meet all password requirements'
        : 'Soddisfa tutti i requisiti della password');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/guest/reset-password`, {
        token,
        password,
        confirm_password: confirmPassword
      });
      setSuccess(true);
      toast.success(language === 'en' 
        ? 'Password reset successful!'
        : 'Password reimpostata con successo!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || (language === 'en' 
        ? 'Error resetting password'
        : 'Errore durante il reset della password'));
    } finally {
      setLoading(false);
    }
  };
  
  const renderValidating = () => (
    <div className="text-center py-12">
      <Loader2 className="w-12 h-12 mx-auto text-[#C5A059] animate-spin mb-4" />
      <p className="text-gray-600">
        {language === 'en' ? 'Validating reset link...' : 'Validazione link in corso...'}
      </p>
    </div>
  );
  
  const renderInvalidToken = () => (
    <div className="text-center space-y-6">
      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
        <XCircle className="w-8 h-8 text-white" />
      </div>
      
      <div>
        <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-2">
          {language === 'en' ? 'Invalid Reset Link' : 'Link Non Valido'}
        </h1>
        <p className="text-gray-600 text-sm">
          {tokenError || (language === 'en' 
            ? 'This password reset link is invalid or has expired.'
            : 'Questo link per il reset della password non è valido o è scaduto.')}
        </p>
      </div>
      
      <div className="space-y-3">
        <Link to="/forgot-password">
          <Button className="w-full bg-[#1B4332] hover:bg-[#2D5A45] text-white">
            {language === 'en' ? 'Request New Link' : 'Richiedi Nuovo Link'}
          </Button>
        </Link>
        
        <Link to="/login">
          <Button variant="ghost" className="w-full text-gray-600">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Back to Login' : 'Torna al Login'}
          </Button>
        </Link>
      </div>
    </div>
  );
  
  const renderForm = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#1B4332] to-[#2D5A45] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <Lock className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-2">
          {language === 'en' ? 'Create New Password' : 'Crea Nuova Password'}
        </h1>
        <p className="text-gray-600 text-sm">
          {language === 'en' 
            ? 'Enter your new password below.'
            : 'Inserisci la tua nuova password qui sotto.'}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium text-gray-700">
            {language === 'en' ? 'New Password' : 'Nuova Password'}
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="pl-10 pr-10 h-12"
              required
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#C5A059]"
              data-testid="toggle-password-btn"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Confirm Password */}
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
            {language === 'en' ? 'Confirm Password' : 'Conferma Password'}
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="pl-10 pr-10 h-12"
              required
              data-testid="reset-confirm-password-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#C5A059]"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Requirements checklist */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {language === 'en' ? 'Password Requirements' : 'Requisiti Password'}
          </p>
          {requirements.map((req, i) => (
            <div 
              key={i} 
              className={`flex items-center gap-2 text-sm ${req.met ? 'text-green-600' : 'text-gray-500'}`}
            >
              {req.met ? (
                <Check className="w-4 h-4" />
              ) : (
                <div className="w-4 h-4 rounded-full border border-current" />
              )}
              <span>{req.text}</span>
            </div>
          ))}
        </div>
        
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        
        <Button
          type="submit"
          disabled={loading || !allRequirementsMet}
          className="w-full h-12 bg-[#1B4332] hover:bg-[#2D5A45] text-white font-medium"
          data-testid="submit-reset-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            language === 'en' ? 'Reset Password' : 'Reimposta Password'
          )}
        </Button>
      </form>
    </div>
  );
  
  const renderSuccess = () => (
    <div className="text-center space-y-6">
      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
        <CheckCircle className="w-8 h-8 text-white" />
      </div>
      
      <div>
        <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-2">
          {language === 'en' ? 'Password Reset!' : 'Password Reimpostata!'}
        </h1>
        <p className="text-gray-600 text-sm">
          {language === 'en' 
            ? 'Your password has been successfully reset. You can now login with your new password.'
            : 'La tua password è stata reimpostata con successo. Ora puoi accedere con la nuova password.'}
        </p>
      </div>
      
      <Link to="/login">
        <Button className="w-full bg-[#1B4332] hover:bg-[#2D5A45] text-white">
          {language === 'en' ? 'Go to Login' : 'Vai al Login'}
        </Button>
      </Link>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardContent className="p-8">
          {validating ? renderValidating() : (
            success ? renderSuccess() : (
              tokenValid ? renderForm() : renderInvalidToken()
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
}
